import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

// Scales text to fill a fixed-height container: as large as possible without overflowing.
//
// This measures rather than estimates. Guessing a size from character count is tempting
// and wrong — the same 400 characters occupy wildly different heights depending on word
// lengths, line-break luck, container width and any markdown structure. So we binary
// search the real rendered height instead.
//
// The search writes `fontSize` straight to the DOM node and reads `scrollHeight` back,
// bypassing React entirely. Each step is a synchronous style-write/layout-read, so the
// whole search costs a handful of reflows inside one layout pass and commits a single
// state update at the end — rather than one render per candidate size.

type TFitTextOptions = {
  minFontSize?: number;
  maxFontSize?: number;
  // Search stops once the bracket is narrower than this. Sub-pixel precision is invisible,
  // so this bounds the iteration count (~6 steps over a 13–28px range).
  precision?: number;
};

const useFitText = (
  // Re-fit when this changes — normally the text being laid out.
  resetKey: unknown,
  { minFontSize = 13, maxFontSize = 28, precision = 0.5 }: TFitTextOptions = {},
) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [fontSize, setFontSize] = useState(maxFontSize);
  // True when the text overflows even at the minimum size — the caller can then fade the
  // cut edge, and skip that treatment when everything actually fits.
  const [isOverflowing, setIsOverflowing] = useState(false);

  const measure = useCallback(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const available = container.clientHeight;
    if (available <= 0) return;

    const fitsAt = (size: number) => {
      content.style.fontSize = `${size}px`;
      return content.scrollHeight <= available;
    };

    let best = minFontSize;
    let overflows = false;

    if (fitsAt(maxFontSize)) {
      best = maxFontSize;
    } else if (!fitsAt(minFontSize)) {
      // Even the floor overflows: stay at the floor and let the caller fade the cut.
      best = minFontSize;
      overflows = true;
    } else {
      let low = minFontSize;
      let high = maxFontSize;

      while (high - low > precision) {
        const mid = (low + high) / 2;
        if (fitsAt(mid)) {
          best = mid;
          low = mid;
        } else {
          high = mid;
        }
      }
    }

    // Hand control back to React, which re-applies the winning size as a prop.
    content.style.fontSize = '';
    // Floor, never round: `best` is a size measured to fit, and rounding it up would
    // reintroduce the overflow the search just eliminated.
    setFontSize(Math.floor(best * 10) / 10);
    setIsOverflowing(overflows);
  }, [maxFontSize, minFontSize, precision]);

  // Layout effect so the fitted size is applied before the browser paints — otherwise the
  // text visibly jumps from the starting size to the fitted one.
  useLayoutEffect(() => {
    measure();
  }, [measure, resetKey]);

  // The container is a flex child of a fixed-height card, so its height changes with the
  // viewport. Re-fit when it does. No feedback loop: the container's height comes from
  // the card, never from the text inside it.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(() => measure());
    observer.observe(container);

    return () => observer.disconnect();
  }, [measure]);

  // Web fonts change text metrics when they land, which invalidates a fit done against
  // the fallback face.
  useEffect(() => {
    if (!document.fonts?.ready) return;

    let isCurrent = true;
    document.fonts.ready.then(() => {
      if (isCurrent) measure();
    });

    return () => {
      isCurrent = false;
    };
  }, [measure]);

  return { containerRef, contentRef, fontSize, isOverflowing };
};

export default useFitText;
